import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  QueryList,
  Renderer2,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { OrderformCartProductItem, ProductListFascade } from '../../fascades/product-list.fascade';
import { BehaviorSubject, combineLatest, firstValueFrom, merge, Observable, Subject } from 'rxjs';
import { CustomFormData, OrderformDiscountDisplayPosition, OrderformProductItem } from '../../ngrx/orderform.models';
import { OrderformFascade } from '../../fascades/orderform.fascade';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, skip, takeUntil, tap } from 'rxjs/operators';
import { OrderBumpService } from '../order-bump/order-bump.service';
import { isPageBuilderDesignMode } from '../../misc/pagebuilder';
import { isEqual } from 'lodash-es';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

export const PRODUCT_LIST_QUANTITY_UPDATE_DEBOUNCE = 200;

export interface ProductListFormInputInterface {
  items: {
    product_id: number;
    quantity: {
      value: number;
    };
    eticket_date_id?: number;
    variant_id: number;
  }[];
}

let _timeoutDiscountDisplayPostionChange;

@Component({
  selector: 'orderform-product-list',
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line
  encapsulation: ViewEncapsulation.None,
})
export class ProductListComponent implements OnDestroy, AfterViewInit {
  static uniqueIdCount = 0;

  private _discountDisplayPositionEnforce$ = new Subject<string>();
  public customFormOnProductChange$ = new BehaviorSubject<{
    [product_id: number]: { [no: number]: CustomFormData };
  }>({});

  @Input() set discount_display_position(v: string) {
    this._discountDisplayPositionEnforce$.next(v);
  }

  @Input() has_wide_texts = true;
  uniqueId: string = 'product-list' + ProductListComponent.uniqueIdCount++;
  public showPriceColumn$ = new BehaviorSubject<boolean>(true);
  @Input() set hide_price_column(b: any) {
    this.showPriceColumn$.next(!coerceBooleanProperty(b));
  }

  public showPreQuantity$ = new BehaviorSubject<boolean>(false);
  @Input() set show_pre_quantity(b: any) {
    this.showPreQuantity$.next(coerceBooleanProperty(b));
  }

  @Input() mobileFontsizeHeadline;
  @Input() mobileFontsizeText;

  private destroy$ = new Subject<void>();

  public form = new FormGroup({
    items: new FormArray([]),
  });

  private _formUpdateDisabled = false;

  mainProduct$ = this._orderformFascade.mainProduct$;

  productList$: Observable<OrderformCartProductItem[]> = combineLatest([
    this._productListFascade.cartProductList$,
    this._orderBumpService.productIdList$,
    this._orderformFascade.config$,
    this._orderformFascade.pending$,
  ]).pipe(
    /**
     * Start comment
     * Need to set debounce to listen for pending$
     * Because we need to update the store first, then we can start update request
     * if debounce not exists, the store update triggers this observable direct
     */
    debounceTime(PRODUCT_LIST_QUANTITY_UPDATE_DEBOUNCE),
    filter(([productList, orderBumpProductList, config, loading]) => {
      // wait until all update Requests completed
      return loading === false;
    }),
    /**
     * End comment
     */
    map(([productList, orderBumpProductList, config, loading]) => {
      const deepCopy: OrderformCartProductItem[] = JSON.parse(JSON.stringify(productList));
      // Hauptprodukt immer im Warenkorb anzeigen list_index === 0
      return deepCopy.filter((productItem) => {
        /**
         * Check is main product in Orderbump
         * If Orderbump Produkt === Main Produkt
         * Allow is_editable!
         */
        if (+productItem.product_id === +config.product_id) {
          return true;
        }

        const isOrderBumpProduct = orderBumpProductList.indexOf(+productItem.product_id) >= 0;
        if (isOrderBumpProduct) {
          // Menge kann nur in der Oder-Bump eingestellt werden.
          productItem.quantity = {
            ...productItem.quantity,
            is_editable: false,
          };
        }
        // Produkt ist *nicht* in einer OrderBump oder Bestellmenge ist größer 0
        return !isOrderBumpProduct || productItem.orderItem.quantity?.value > 0;
      });
    }),
    tap((list) => {
      const formItems = this.form.get('items') as FormArray;
      this._formUpdateDisabled = true;

      list
        .sort((a, b) => a.orderItem.list_index - b.orderItem.list_index)
        .forEach((product, index) => {
          const formItemIndex = formItems.value.findIndex((fi) => '' + fi.product_id === '' + product.product_id);

          if (formItemIndex === -1) {
            // no form item found, create new!
            const formItem = new FormGroup({
              product_id: new FormControl(product.product_id),
              quantity: new FormGroup({
                value: new FormControl<number>(0, { nonNullable: true }),
              }),
              eticket_date_id: new FormControl<number>(0, {
                nonNullable: true,
              }),
              variant_id: new FormControl(0, { nonNullable: true }),
            });
            formItems.insert(index, formItem);
            formItem.controls.quantity.controls.value.valueChanges
              .pipe(this._filterComponentIsActive(), takeUntil(this.destroy$))
              .subscribe((quantity: number) => {
                this._productListFascade.updateQuantity(product.product_id, quantity);
              });
            formItem.controls.eticket_date_id.valueChanges
              .pipe(this._filterComponentIsActive(), takeUntil(this.destroy$))
              .subscribe((eTicketId: number) => {
                this._productListFascade.updateETicketId(product.product_id, eTicketId);
              });
            formItem.controls.variant_id.valueChanges
              .pipe(this._filterComponentIsActive(), takeUntil(this.destroy$))
              .subscribe((variantId: number) => {
                this._productListFascade.updateVariantId(product.product_id, variantId);
              });
          } else if (index !== formItemIndex) {
            //item found but positioning incorrect, move it!
            const formItem = formItems.at(formItemIndex);
            formItems.removeAt(formItemIndex);
            formItems.insert(index, formItem);
          }

          const itemValue = {
            product_id: product.product_id,
            quantity: {
              value: product.orderItem.quantity?.value || 0,
            },
            eticket_date_id: +product.orderItem.eticket_date_id || null,
            variant_id: +product.orderItem.variant_id || null,
          };

          if (!isEqual(formItems.at(index).value, itemValue)) {
            (formItems.at(index) as FormGroup).setValue(itemValue, {
              emitEvent: false,
            });
          }
        });

      // Remove all excess formItems
      for (let i = formItems.length - 1; i >= list.length; i--) {
        formItems.removeAt(i);
      }

      this._formUpdateDisabled = false;
    })
  );

  showAmount$ = this._productListFascade.cartProductList$.pipe(
    map((data) => {
      return data.length > 1 || data[0].quantity.max_value > 1;
    }),
    shareReplay(1)
  );

  productListSettings$ = this._productListFascade.settings$;

  @ViewChildren('eticketInputComponent') eticketInputComponent: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('variantInputComponent') variantInputComponent: QueryList<ElementRef<HTMLElement>>;

  private _discountDisplayPosition$ = merge(
    this._orderformFascade.order$.pipe(map((order) => order.order.discount_display_position)),
    this._discountDisplayPositionEnforce$
  ).pipe(distinctUntilChanged());
  public showDiscountOnItem$ = this._discountDisplayPosition$.pipe(map((position) => position.indexOf('item') >= 0));

  public customFormOnProductFilled$: {
    [key: number]: BehaviorSubject<CustomFormData>;
  } = {};

  constructor(
    private _productListFascade: ProductListFascade,
    private _orderformFascade: OrderformFascade,
    private _orderBumpService: OrderBumpService,
    private _renderer: Renderer2,
    private _ngZone: NgZone,
    private _elementRef: ElementRef
  ) {
    // initialize custom forms
    this._productListFascade.cartProductList$.pipe(takeUntil(this.destroy$)).subscribe((cartList) => {
      const initialCustomFormData = {};
      cartList.forEach((product) => {
        initialCustomFormData[product.product_id] = {};
        product.customForms.forEach((form) => {
          const key = form.no + '-form-' + product.product_id;
          if (!this.customFormOnProductFilled$[key]) {
            this.customFormOnProductFilled$[key] = new BehaviorSubject<CustomFormData>(
              product.orderItem.customForm ? product.orderItem.customForm[form.no] : {}
            );
          }
          initialCustomFormData[product.product_id][form.no] = this.customFormOnProductFilled$[key].value;
        });
      });

      this._formUpdateDisabled = true;
      this.customFormOnProductChange$.next(initialCustomFormData);
      this._formUpdateDisabled = false;
    });

    /**
     * Attention!
     * Dirty... because it can cause recursive calls. update -> save data -> get new data => update ...
     * Update only data for display position in editormode mode!!!
     */
    this._discountDisplayPosition$
      .pipe(
        filter((e) => isPageBuilderDesignMode()),
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        skip(1)
      )
      .subscribe((position) => {
        clearTimeout(_timeoutDiscountDisplayPostionChange);
        this._ngZone.runOutsideAngular(() => {
          _timeoutDiscountDisplayPostionChange = setTimeout(() => {
            this._orderformFascade.setOrderStore({
              discount_display_position: position as OrderformDiscountDisplayPosition,
            });
            this._ngZone.run(() => {
              firstValueFrom(this._orderformFascade.update$());
            });
          });
        });
      });

    this.customFormOnProductChange$
      .pipe(
        skip(1),
        filter(() => {
          return !this._formUpdateDisabled;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((customFormByProduct) => {
        Object.keys(customFormByProduct).forEach((product_id) => {
          this._productListFascade.updateCustomForms(parseInt(product_id, 10), customFormByProduct[product_id]);
        });
      });
  }

  getProductDescription(product: OrderformProductItem): string {
    const description = typeof product.description === 'string' ? product.description : product.product_description;

    return description.replace(
      '[STOCK]',
      '' + Math.max(0, product.sales_left ? product.sales_left : product.product_sales_left)
    );
  }

  trackByProductId(index, product: OrderformCartProductItem): string {
    return product.product_id + '-' + product.quantity.is_editable;
  }

  trackByOptionId(index: number, option: { id: number | 'custom' }): string {
    return '' + option.id;
  }

  ngAfterViewInit() {
    // initialisiere Komponenten mit Platzhalter im Template Code
    [
      {
        components: this.eticketInputComponent,
        selector: '.eticket-input-component',
      },
      {
        components: this.variantInputComponent,
        selector: '.variant-input-component',
      },
    ].forEach((init) => {
      init.components.forEach((item) => {
        const placeholderSpan = item.nativeElement.parentNode.querySelector(init.selector) as HTMLSpanElement;
        this._renderer.insertBefore(placeholderSpan.parentNode, item.nativeElement, placeholderSpan);
        this._renderer.removeChild(placeholderSpan.parentNode, placeholderSpan);
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  setCustomFormOnProduct(product_id: number, form_no: number, key: string, data: CustomFormData) {
    this.customFormOnProductFilled$[key].next({
      ...this.customFormOnProductFilled$[key].value,
      ...data,
    });
    const formData = {
      ...this.customFormOnProductChange$.value,
      [product_id]: {
        ...(this.customFormOnProductChange$.value[product_id] ?? {}),
        [form_no]: this.customFormOnProductFilled$[key].value,
      },
    };
    this.customFormOnProductChange$.next(formData);
  }

  private _filterComponentIsActive() {
    return filter(() => {
      const { width, height } = this._elementRef.nativeElement.getBoundingClientRect();
      return !this._formUpdateDisabled && width > 0 && height > 0;
    });
  }
}
